import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logoImage from '../assets/Logo_dash.png';
import {
  BarChart3,
  Users,
  Truck,
  FileBarChart,
  FileText,
  Settings,
  File,
  Package,
  ChevronDown,
} from "lucide-react"

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

type MenuChild = {
  id: string;
  label: string;
  path: string;
};

type MenuItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuChild[];
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(() => ({
    'jd-instant': location.pathname.startsWith('/jd-instant'),
  }));

  const menuItems: MenuItem[] = [
    { id: "dashboard", label: "Dashboard", icon: <BarChart3 />, path: "/dashboard" },
    {
      id: "jd-instant",
      label: "JD Instant",
      icon: <Package />,
      children: [
        { id: "jd-instant-trips", label: "Trip", path: "/jd-instant" },
        { id: "jd-instant-mis", label: "MIS Report", path: "/jd-instant/mis-report" },
      ],
    },
    { id: "drivers", label: "Manage Drivers", icon: <Users />, path: "/drivers" },
    { id: "vehicles", label: "Manage Vehicles", icon: <Truck />, path: "/vehicles" },
    { id: "reports", label: "Report", icon: <FileBarChart />, path: "/reports" },
    { id: "invoice-list", label: "Invoice List", icon: <File />, path: "/invoice-list" },
    { id: "invoice", label: "Upload Invoice", icon: <FileText />, path: "/invoice" },
    { id: "profile", label: "Settings", icon: <Settings />, path: "/profile" },
  ];

  const handleMenuClick = (path: string) => {
    navigate(path);
    onClose();
  };

  const isPathActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === path || location.pathname === '/';
    }
    if (path === '/jd-instant') {
      return (
        location.pathname === '/jd-instant' ||
        /^\/jd-instant\/[a-f0-9]{24}$/i.test(location.pathname)
      );
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isGroupActive = (item: MenuItem) => {
    if (item.children?.length) {
      return item.children.some((child) => isPathActive(child.path));
    }
    return item.path ? isPathActive(item.path) : false;
  };

  const toggleMenu = (id: string) => {
    setOpenMenus((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderMenuItem = (item: MenuItem, isMobile = false) => {
    if (item.children?.length) {
      const groupActive = isGroupActive(item);
      const isOpenMenu = openMenus[item.id] ?? groupActive;

      return (
        <div key={item.id}>
          <button
            type="button"
            onClick={() => toggleMenu(item.id)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-colors ${
              groupActive
                ? isMobile
                  ? 'bg-jd-green text-white border-l-4 border-green-400'
                  : 'bg-jd-green text-white'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center space-x-3">
              <span className="text-lg">{item.icon}</span>
              <span className="font-medium">{item.label}</span>
            </span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isOpenMenu ? 'rotate-180' : ''}`}
            />
          </button>
          {isOpenMenu ? (
            <div className="ml-6 mt-1 space-y-1">
              {item.children.map((child) => {
                const childActive = isPathActive(child.path);
                return (
                  <button
                    key={child.id}
                    type="button"
                    onClick={() => handleMenuClick(child.path)}
                    className={`w-full rounded-lg px-4 py-2 text-left text-sm font-medium transition-colors ${
                      childActive
                        ? 'bg-green-50 text-jd-green border-l-4 border-jd-green pl-[calc(1rem-4px)]'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {child.label}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      );
    }

    const active = item.path ? isPathActive(item.path) : false;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => item.path && handleMenuClick(item.path)}
        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors relative ${
          active
            ? isMobile
              ? 'bg-jd-green text-white border-l-4 border-green-400'
              : 'bg-jd-green text-white'
            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        <span className="text-lg">{item.icon}</span>
        <span className="font-medium">{item.label}</span>
      </button>
    );
  };

  return (
    <>
      {/* Mobile sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white text-gray-900 transform transition-transform duration-300 ease-in-out lg:hidden ${isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center justify-center flex-1">
                <img src={logoImage} alt="JustDeliveries Logo" className="h-10 w-25" />
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 lg:hidden"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-2">
              {menuItems.map((item) => renderMenuItem(item, true))}
            </div>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:bg-white lg:text-gray-900 lg:h-full lg:flex-col">
        <div className="pb-4 pt-3 border-b border-gray-200">
          <div className="flex items-center justify-center">
            <img src={logoImage} alt="JustDeliveries Logo" className="h-12 w-22" />
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-2">
            {menuItems.map((item) => renderMenuItem(item))}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
